import PropTypes from "prop-types";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";

import { useCart } from "context/CartContext";
import { useMobileLocale } from "mobile/i18n";
import logo from "assets/images/logos/ShortLogo.png";

/** Storefront account palette — matches VBS / MX mobile tokens, not staff MUI blue. */
export const portalChrome = {
  canvas: "#F7F8FA",
  surface: "#FFFFFF",
  ink: "#1A1A1F",
  muted: "#55575C",
  subtle: "#6E7278",
  line: "rgba(26,26,31,0.1)",
  accent: "#FF5C00",
  accentSoft: "rgba(255, 92, 0, 0.12)",
  accentHover: "#E04F00",
  /** Clearer near-black for icons — not muddy charcoal. */
  iconInk: "#242428",
  iconInkSoft: "rgba(36, 36, 40, 0.08)",
  /** Brand red for hub quick-action / list icon accents. */
  brandRed: "#E01820",
  brandRedSoft: "rgba(224, 24, 32, 0.1)",
  danger: "#C4151C",
  dangerSoft: "rgba(196, 21, 28, 0.09)",
  swoosh: "linear-gradient(90deg, #FFB000 0%, #FF5C00 35%, #FF1A1A 70%, #D70000 100%)",
  lift: "0 12px 28px rgba(26,26,31,0.06)",
};

/** Glyph color only — no filled icon tiles. */
function iconToneColor(tone) {
  if (tone === "red") return portalChrome.brandRed;
  if (tone === "danger") return portalChrome.danger;
  return portalChrome.iconInk;
}

function resolveBackPath(pathname) {
  const path = pathname || "/m/account";
  if (path.includes("/chat")) return path.replace(/\/chat$/, "");
  // Hub first: "/core-returns" must not match the "/core-return" wizard segment.
  if (path === "/m/account/core-returns" || path === "/m/account/core-returns/") {
    return "/m/account";
  }
  if (/\/core-return\/?$/.test(path)) {
    return "/m/account/core-returns";
  }
  if (path.includes("/warranty")) {
    return path.replace(/\/warranty$/, "");
  }
  if (path.startsWith("/m/account/orders/")) return "/m/account/orders";
  if (path === "/m/account/orders") return "/m/account";
  if (path.startsWith("/m/account/distributor-requests/")) return "/m/account/distributor-requests";
  if (path === "/m/account/distributor-requests") return "/m/account";
  if (path === "/m/account/distributor-customers") return "/m/account";
  if (path.startsWith("/m/account/valve-bodies/")) return "/m/account/valve-bodies";
  if (path === "/m/account/valve-bodies") return "/m/account";
  if (/\/warranty-claims\/[^/]+$/.test(path)) return "/m/account/valve-bodies";
  if (path === "/m/account/support/new" || path.startsWith("/m/account/support/")) return "/m/account/support";
  if (path === "/m/account/support") return "/m/account";
  if (path === "/m/account/profile" || path === "/m/account/addresses") return "/m/account";
  return "/m/account";
}

export const accountFieldSx = {
  bgcolor: portalChrome.surface,
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: portalChrome.surface,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: portalChrome.line,
  },
};

export const accountPrimaryBtnSx = {
  borderRadius: "12px",
  fontWeight: 800,
  textTransform: "none",
  bgcolor: portalChrome.brandRed,
  color: "#fff",
  boxShadow: "none",
  "&:hover": { bgcolor: "#C4141C", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "rgba(196,21,28,0.35)", color: "#fff" },
};

export const accountSecondaryBtnSx = {
  borderRadius: "12px",
  fontWeight: 750,
  textTransform: "none",
  borderColor: portalChrome.line,
  color: portalChrome.iconInk,
  bgcolor: portalChrome.surface,
  "&:hover": { borderColor: portalChrome.brandRed, bgcolor: portalChrome.brandRedSoft },
};

/**
 * Clean account chrome: hub shows logo only; sub-pages show back + title.
 * Header icon clusters are intentionally omitted — quick actions live in the hub body.
 */
export function PortalAccountShell({ title, subtitle, children, action, headerAction = null, hub = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useMobileLocale();
  const showBack = !hub && !["/m/account", "/m/account/"].includes(location.pathname);
  const backPath = useMemo(() => resolveBackPath(location.pathname), [location.pathname]);

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: portalChrome.canvas, color: portalChrome.ink, pb: 5 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          bgcolor: "rgba(247,248,250,0.92)",
          backdropFilter: "blur(18px)",
          borderBottom: `1px solid ${portalChrome.line}`,
          pt: "env(safe-area-inset-top)",
        }}
      >
        <Box sx={{ height: 3, background: portalChrome.swoosh }} />
        <Box
          sx={{
            px: 2,
            py: hub ? 1.1 : 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: hub ? 52 : 56,
          }}
        >
          {showBack ? (
            <ButtonBase
              onClick={() => {
                if (backPath) {
                  navigate(backPath);
                  return;
                }
                if (window.history.length > 1) navigate(-1);
                else navigate("/m/account");
              }}
              aria-label={t("Back")}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: portalChrome.surface,
                border: `1px solid ${portalChrome.line}`,
                color: portalChrome.ink,
                flexShrink: 0,
                transition: "transform 0.15s ease, background-color 0.15s ease",
                "&:active": { transform: "scale(0.94)" },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>arrow_back_ios_new</Icon>
            </ButtonBase>
          ) : (
            <Box
              component="img"
              src={logo}
              alt="VBS"
              onClick={() => navigate("/m")}
              sx={{
                width: hub ? 64 : 52,
                height: "auto",
                display: "block",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          )}

          {!hub ? (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 800,
                  letterSpacing: -0.35,
                  lineHeight: 1.15,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t(title)}
              </Typography>
              {subtitle ? (
                <Typography
                  sx={{
                    color: portalChrome.muted,
                    fontSize: 12.5,
                    fontWeight: 600,
                    mt: 0.15,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {t(subtitle)}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}

          {headerAction ? <Box sx={{ flexShrink: 0 }}>{headerAction}</Box> : null}

          {hub && !headerAction ? (
            <ButtonBase
              onClick={() => navigate("/m/catalog")}
              sx={{
                px: 1.25,
                py: 0.7,
                borderRadius: "999px",
                bgcolor: portalChrome.accentSoft,
                color: portalChrome.accentHover,
                fontSize: 13,
                fontWeight: 750,
                letterSpacing: 0.01,
                transition: "transform 0.15s ease, background-color 0.15s ease",
                "&:active": { transform: "scale(0.96)" },
              }}
            >
              {t("Shop")}
            </ButtonBase>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ px: 2, pt: hub ? 0.5 : 1.25, maxWidth: 560, mx: "auto" }}>
        {action ? <Box sx={{ mb: 1.25 }}>{action}</Box> : null}
        {children}
      </Box>
    </Box>
  );
}

PortalAccountShell.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  action: PropTypes.node,
  headerAction: PropTypes.node,
  hub: PropTypes.bool,
};

PortalAccountShell.defaultProps = {
  title: "",
  subtitle: "",
  action: null,
  headerAction: null,
  hub: false,
};

export function AccountIdentity({ name, email, roleLabel }) {
  const { t } = useMobileLocale();
  const initials = String(name || email || "A")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "A";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.75,
        animation: "portalFadeUp 0.35s ease both",
        "@keyframes portalFadeUp": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "18px",
          background: portalChrome.swoosh,
          display: "grid",
          placeItems: "center",
          color: "#fff",
          fontWeight: 850,
          fontSize: 18,
          letterSpacing: -0.4,
          boxShadow: "0 10px 22px rgba(255,92,0,0.28)",
          flexShrink: 0,
        }}
      >
        {initials}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 820,
            letterSpacing: -0.55,
            lineHeight: 1.1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name || t("Account")}
        </Typography>
        {email ? (
          <Typography
            sx={{
              color: portalChrome.muted,
              fontSize: 13.5,
              fontWeight: 600,
              mt: 0.35,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {email}
          </Typography>
        ) : null}
        {roleLabel ? (
          <Typography
            sx={{
              color: portalChrome.subtle,
              fontSize: 11.5,
              fontWeight: 750,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              mt: 0.45,
            }}
          >
            {roleLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

AccountIdentity.propTypes = {
  name: PropTypes.string,
  email: PropTypes.string,
  roleLabel: PropTypes.string,
};

AccountIdentity.defaultProps = {
  name: "",
  email: "",
  roleLabel: "",
};

export function QuickActionBar({ actions }) {
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const { t } = useMobileLocale();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${Math.min(actions.length, 4)}, 1fr)`,
        gap: 0.5,
        px: 0.5,
        py: 0.85,
        mb: 2,
        borderRadius: "18px",
        bgcolor: portalChrome.surface,
        border: `1px solid ${portalChrome.line}`,
        boxShadow: portalChrome.lift,
        animation: "portalFadeUp 0.4s ease 0.05s both",
        "@keyframes portalFadeUp": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {actions.map((action) => {
        const badge = action.badge === "cart" ? itemCount : action.badge;
        const iconColor = iconToneColor(action.tone || "ink");
        const labelColor =
          action.tone === "danger" ? portalChrome.danger : portalChrome.iconInk;
        return (
          <ButtonBase
            key={action.key}
            onClick={() => {
              if (typeof action.onClick === "function") {
                action.onClick();
                return;
              }
              if (action.to) navigate(action.to);
            }}
            aria-label={t(action.label)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              py: 1,
              px: 0.25,
              borderRadius: "14px",
              minHeight: 80,
              transition: "background-color 0.15s ease, transform 0.15s ease",
              "&:hover": { bgcolor: "rgba(26,26,31,0.03)" },
              "&:active": {
                transform: "scale(0.96)",
                bgcolor: "rgba(26,26,31,0.04)",
              },
            }}
          >
            <Badge
              badgeContent={badge || 0}
              color="error"
              invisible={!badge}
              sx={{
                "& .MuiBadge-badge": {
                  fontWeight: 800,
                  fontSize: 10,
                  minWidth: 16,
                  height: 16,
                  top: 0,
                  right: -2,
                  bgcolor: portalChrome.brandRed,
                },
              }}
            >
              <Icon sx={{ fontSize: 38, color: iconColor, lineHeight: 1 }}>{action.icon}</Icon>
            </Badge>
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 750,
                color: labelColor,
                lineHeight: 1.15,
                textAlign: "center",
              }}
            >
              {t(action.label)}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

QuickActionBar.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      to: PropTypes.string,
      onClick: PropTypes.func,
      tone: PropTypes.oneOf(["ink", "red", "danger"]),
      badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
};

export function AccountListGroup({ title, children }) {
  const { t } = useMobileLocale();
  return (
    <Box sx={{ mb: 2 }}>
      {title ? (
        <Typography
          sx={{
            px: 0.5,
            mb: 0.75,
            fontSize: 11.5,
            fontWeight: 800,
            letterSpacing: 0.9,
            textTransform: "uppercase",
            color: portalChrome.subtle,
          }}
        >
          {t(title)}
        </Typography>
      ) : null}
      <Box
        sx={{
          bgcolor: portalChrome.surface,
          border: `1px solid ${portalChrome.line}`,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: portalChrome.lift,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

AccountListGroup.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
};

AccountListGroup.defaultProps = {
  title: "",
};

export function AccountListRow({
  icon,
  title,
  subtitle,
  meta = "",
  badge = null,
  onClick,
  danger = false,
  divider = true,
}) {
  const { t } = useMobileLocale();
  const badgeCount = Number(badge || 0);
  return (
    <Box>
      <ButtonBase
        onClick={onClick}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.35,
          py: subtitle ? 1.25 : 1.2,
          textAlign: "left",
          transition: "background-color 0.15s ease",
          "&:active": { bgcolor: danger ? portalChrome.dangerSoft : "rgba(15,15,18,0.035)" },
        }}
      >
        {icon ? (
          <Icon
            sx={{
              fontSize: 34,
              color: danger ? portalChrome.danger : portalChrome.iconInk,
              flexShrink: 0,
              width: 36,
              lineHeight: 1,
            }}
          >
            {icon}
          </Icon>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
            <Typography
              sx={{
                fontSize: 15.5,
                fontWeight: badgeCount > 0 ? 820 : 700,
                color: danger ? portalChrome.danger : portalChrome.ink,
                lineHeight: 1.25,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {t(title)}
            </Typography>
            {meta ? (
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: badgeCount > 0 ? 800 : 650,
                  color: badgeCount > 0 ? portalChrome.accentHover : portalChrome.subtle,
                  flexShrink: 0,
                }}
              >
                {meta}
              </Typography>
            ) : null}
          </Box>
          {subtitle ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.2 }}>
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: portalChrome.muted,
                  lineHeight: 1.35,
                  minWidth: 0,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: badgeCount > 0 ? 700 : 500,
                }}
              >
                {t(subtitle)}
              </Typography>
              {badgeCount > 0 ? (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.55,
                    borderRadius: "999px",
                    bgcolor: portalChrome.brandRed,
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 800,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {badgeCount > 99 ? "99+" : badgeCount}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>
        {!danger && badgeCount <= 0 ? (
          <Icon sx={{ color: portalChrome.subtle, fontSize: 22, flexShrink: 0 }}>chevron_right</Icon>
        ) : null}
      </ButtonBase>
      {divider ? <Box sx={{ ml: icon ? 6.5 : 1.35, height: 1, bgcolor: portalChrome.line }} /> : null}
    </Box>
  );
}

AccountListRow.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  meta: PropTypes.string,
  badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClick: PropTypes.func.isRequired,
  danger: PropTypes.bool,
  divider: PropTypes.bool,
};

AccountListRow.defaultProps = {
  icon: "",
  subtitle: "",
  meta: "",
  badge: null,
  danger: false,
  divider: true,
};

/**
 * Modern pill filter chips for account sub-pages — large bare icons, no square tiles.
 */
export function AccountFilterBar({ label, value, options, onChange }) {
  const { t } = useMobileLocale();
  const selectedOption = options.find((option) => option.key === value);

  return (
    <Box sx={{ mb: 0.25 }}>
      {label ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.75,
            px: 0.25,
          }}
        >
          <Typography
            sx={{
              color: portalChrome.subtle,
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: 0.85,
              textTransform: "uppercase",
            }}
          >
            {t(label)}
          </Typography>
          {selectedOption ? (
            <Typography sx={{ color: portalChrome.ink, fontSize: 12.5, fontWeight: 750 }}>
              {t(selectedOption.label)}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <Box
        sx={{
          display: "flex",
          gap: 0.65,
          overflowX: "auto",
          pb: 0.35,
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {options.map((option) => {
          const selected = value === option.key;
          const accent =
            option.accent === "red"
              ? portalChrome.brandRed
              : option.accent === "green"
              ? "#147A5C"
              : option.accent === "orange"
              ? portalChrome.accent
              : portalChrome.iconInk;
          return (
            <ButtonBase
              key={String(option.key)}
              onClick={() => onChange(option.key)}
              sx={{
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                gap: 0.7,
                minHeight: 48,
                px: 1.35,
                py: 0.9,
                borderRadius: "999px",
                border: `1px solid ${selected ? "rgba(224,24,32,0.22)" : portalChrome.line}`,
                bgcolor: selected ? portalChrome.brandRedSoft : portalChrome.surface,
                color: selected ? portalChrome.brandRed : portalChrome.iconInk,
                transition: "background-color 0.15s ease, transform 0.15s ease",
                "&:active": { transform: "scale(0.97)" },
              }}
            >
              {option.icon ? (
                <Icon sx={{ fontSize: 28, color: selected ? portalChrome.brandRed : accent, lineHeight: 1 }}>
                  {option.icon}
                </Icon>
              ) : null}
              <Typography sx={{ fontSize: 13.5, fontWeight: 750, whiteSpace: "nowrap", lineHeight: 1.1 }}>
                {t(option.label)}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

AccountFilterBar.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      accent: PropTypes.oneOf(["orange", "red", "green"]),
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

AccountFilterBar.defaultProps = {
  label: "",
};

/**
 * Hub quick actions: Home · Cart · Scan (register unit via QR) · Logout.
 * List destinations (Orders, Units, Profile, etc.) stay in the grouped listview.
 * Scan opens `/m/scan?from=account` → `/register/valve-body/:tag?qr=1&web=1&from=account`
 * (existing session skips login/signup in RegisterValveBody).
 */
export function buildAccountQuickActions({ onLogout } = {}) {
  return [
    { key: "home", label: "Home", icon: "home", to: "/m", tone: "ink" },
    { key: "cart", label: "Cart", icon: "shopping_cart", to: "/m/cart", badge: "cart", tone: "red" },
    {
      key: "scan",
      label: "Scan",
      icon: "qr_code_scanner",
      to: "/m/scan?from=account",
      tone: "red",
    },
    {
      key: "logout",
      label: "Logout",
      icon: "logout",
      tone: "danger",
      onClick: typeof onLogout === "function" ? onLogout : undefined,
    },
  ];
}

export function AccountSection({ title, children, sx = {} }) {
  const { t } = useMobileLocale();
  return (
    <Box
      sx={{
        bgcolor: portalChrome.surface,
        border: `1px solid ${portalChrome.line}`,
        borderRadius: "16px",
        p: 1.5,
        mb: 1.15,
        boxShadow: portalChrome.lift,
        ...sx,
      }}
    >
      {title ? (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: portalChrome.subtle,
            mb: 1,
          }}
        >
          {t(title)}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

AccountSection.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

AccountSection.defaultProps = {
  title: "",
  sx: {},
};

export function AccountChipRow({ chips }) {
  const { t } = useMobileLocale();
  if (!chips?.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.65, rowGap: 0.65 }}>
      {chips.filter(Boolean).map((chip, index) => {
        const raw = typeof chip === "object" && chip !== null ? chip.label : chip;
        const label = typeof raw === "string" ? t(raw) : raw;
        const emphasis = typeof chip === "object" && chip !== null ? chip.emphasis : index === 0;
        return (
          <Box
            key={`${String(label)}-${index}`}
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: "999px",
              fontSize: 11.5,
              fontWeight: 750,
              lineHeight: 1.2,
              bgcolor: emphasis ? portalChrome.brandRedSoft : portalChrome.iconInkSoft,
              color: emphasis ? portalChrome.brandRed : portalChrome.iconInk,
              border: `1px solid ${emphasis ? "rgba(224,24,32,0.18)" : portalChrome.line}`,
            }}
          >
            {label}
          </Box>
        );
      })}
    </Box>
  );
}

AccountChipRow.propTypes = {
  chips: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.shape({
        label: PropTypes.node,
        emphasis: PropTypes.bool,
      }),
    ])
  ),
};

AccountChipRow.defaultProps = {
  chips: [],
};

export function AccountRecordCard({ title, meta, chips, onClick, children }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: "100%",
        textAlign: "left",
        display: "block",
        borderRadius: "16px",
        mb: 1,
        bgcolor: portalChrome.surface,
        border: `1px solid ${portalChrome.line}`,
        boxShadow: portalChrome.lift,
        transition: "transform 0.15s ease, background-color 0.15s ease",
        "&:active": { transform: "scale(0.985)", bgcolor: "rgba(26,26,31,0.02)" },
      }}
    >
      <Box sx={{ p: 1.45 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 15.5,
                letterSpacing: -0.2,
                lineHeight: 1.25,
                color: portalChrome.ink,
              }}
            >
              {title}
            </Typography>
            {meta ? (
              <Typography sx={{ color: portalChrome.muted, fontSize: 12.5, mt: 0.3, fontWeight: 600 }}>
                {meta}
              </Typography>
            ) : null}
          </Box>
          <Icon sx={{ color: portalChrome.subtle, fontSize: 20, flexShrink: 0, mt: 0.15 }}>chevron_right</Icon>
        </Box>
        {chips?.length ? (
          <Box sx={{ mt: 1 }}>
            <AccountChipRow chips={chips} />
          </Box>
        ) : null}
        {children ? <Box sx={{ mt: 1 }}>{children}</Box> : null}
      </Box>
    </ButtonBase>
  );
}

AccountRecordCard.propTypes = {
  title: PropTypes.node.isRequired,
  meta: PropTypes.node,
  chips: PropTypes.arrayOf(PropTypes.node),
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node,
};

AccountRecordCard.defaultProps = {
  meta: null,
  chips: [],
  children: null,
};

export function AccountDetailRow({ label, value, first = false }) {
  const { t } = useMobileLocale();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 1.25,
        py: 0.85,
        borderTop: first ? "none" : `1px solid ${portalChrome.line}`,
      }}
    >
      <Typography sx={{ color: portalChrome.muted, fontSize: 13, fontWeight: 700 }}>{t(label)}</Typography>
      <Typography
        sx={{
          color: portalChrome.ink,
          fontSize: 13.5,
          fontWeight: 750,
          textAlign: "right",
          minWidth: 0,
          whiteSpace: "pre-wrap",
        }}
      >
        {typeof value === "string" ? t(value || "Pending") : value || t("Pending")}
      </Typography>
    </Box>
  );
}

AccountDetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  first: PropTypes.bool,
};

AccountDetailRow.defaultProps = {
  value: "Pending",
  first: false,
};

export function AccountActionTile({ icon, title, text, onClick, tone = "ink" }) {
  const { t } = useMobileLocale();
  const iconColor = iconToneColor(tone === "red" || tone === "danger" ? tone : "ink");
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        p: 1.35,
        borderRadius: "16px",
        bgcolor: portalChrome.surface,
        border: `1px solid ${portalChrome.line}`,
        boxShadow: portalChrome.lift,
        textAlign: "left",
        transition: "transform 0.15s ease, background-color 0.15s ease",
        "&:active": { transform: "scale(0.985)", bgcolor: "rgba(26,26,31,0.03)" },
      }}
    >
      <Icon sx={{ fontSize: 36, color: iconColor, flexShrink: 0, mt: 0.05, lineHeight: 1 }}>{icon}</Icon>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: portalChrome.ink, lineHeight: 1.2 }}>
          {t(title)}
        </Typography>
        {text ? (
          <Typography sx={{ fontSize: 12.5, color: portalChrome.muted, mt: 0.35, lineHeight: 1.35, fontWeight: 600 }}>
            {t(text)}
          </Typography>
        ) : null}
      </Box>
      <Icon sx={{ color: portalChrome.subtle, fontSize: 22, flexShrink: 0, mt: 0.25 }}>chevron_right</Icon>
    </ButtonBase>
  );
}

AccountActionTile.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  tone: PropTypes.oneOf(["ink", "red", "danger", "dark"]),
};

AccountActionTile.defaultProps = {
  text: "",
  tone: "ink",
};

export function AccountNotice({ query, emptyText, hasRows }) {
  const { t } = useMobileLocale();
  if (query?.isLoading) {
    return (
      <Box
        sx={{
          borderRadius: "14px",
          border: `1px solid ${portalChrome.line}`,
          bgcolor: portalChrome.surface,
          px: 1.5,
          py: 1.35,
          mb: 1,
          color: portalChrome.muted,
          fontSize: 13.5,
          fontWeight: 650,
        }}
      >
        {t("Loading...")}
      </Box>
    );
  }
  if (query?.error) {
    return (
      <Box
        sx={{
          borderRadius: "14px",
          border: `1px solid rgba(196,21,28,0.22)`,
          bgcolor: portalChrome.dangerSoft,
          px: 1.5,
          py: 1.35,
          mb: 1,
          color: portalChrome.danger,
          fontSize: 13.5,
          fontWeight: 650,
        }}
      >
        {query.error?.response?.data?.error?.message || query.error?.message || t("Unable to load.")}
      </Box>
    );
  }
  if (!hasRows) {
    return (
      <Box
        sx={{
          borderRadius: "14px",
          border: `1px solid ${portalChrome.line}`,
          bgcolor: portalChrome.surface,
          px: 1.5,
          py: 1.75,
          mb: 1,
          color: portalChrome.muted,
          fontSize: 13.5,
          fontWeight: 650,
          textAlign: "center",
        }}
      >
        {t(emptyText)}
      </Box>
    );
  }
  return null;
}

AccountNotice.propTypes = {
  query: PropTypes.shape({
    isLoading: PropTypes.bool,
    error: PropTypes.shape({
      message: PropTypes.string,
      response: PropTypes.shape({
        data: PropTypes.shape({
          error: PropTypes.shape({
            message: PropTypes.string,
          }),
        }),
      }),
    }),
  }),
  emptyText: PropTypes.string.isRequired,
  hasRows: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
};

AccountNotice.defaultProps = {
  query: null,
};
